import { FC, useState, Fragment } from "react";
import cx from "classnames";
import { MobileModal } from "@/components/Modal";
import { MobilePaperTitle } from "@/components/PageTitle";
import Typography from "@/components/Typography";
import {
  useCategoriesQuery,
  SortValues,
  useCoursesQuery,
  ProgressValues,
  useCourseProgressesQuery,
  usePaginatedItemsQuery,
  Category,
} from "@/fetch/academy";
import { Skeleton } from "@/components/Loader";
import Tag from "@/components/Tag";
import { SearchTextField } from "@/components/TextField";
import { useDebounce } from "@/hooks";
import { ZigzagArrowIcon } from "@/components/Icon";
import Button from "@/components/Button";
import Divider from "@/components/Divider";

import CategoryCoursesModal, {
  CourseLoader,
  CourseDetails,
} from "./CategoryCoursesModal";
import styles from "./MobileFilterSection.module.scss";
import { INVISIBLE_CATEGORIES } from "./Constants";

enum FilterStates {
  Filter = "Filter",
  Search = "Search",
}

type Props = {
  onClose: () => void;
};

const MobileFilterSection: FC<Props> = ({ onClose }) => {
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const debouncedSearchTerm = useDebounce<string>(searchKeyword, 500);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    undefined
  );
  const [filterState, setFilterState] = useState<FilterStates>(
    FilterStates.Filter
  );
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategoriesQuery();
  const categories = categoriesData?.response || [];
  const visibleCategories = categories.filter(
    (eachCategory) =>
      !Boolean(INVISIBLE_CATEGORIES.find((each) => each === eachCategory.name))
  );

  const { data: coursesData, isLoading: isLoadingCourses } = useCoursesQuery({
    pageNum: 1,
    pageSize: 100,
    sort: SortValues.added,
    keyword: debouncedSearchTerm,
  });

  const courses = coursesData?.response.results || [];

  return (
    <>
      <MobileModal>
        <MobilePaperTitle
          title={
            filterState === FilterStates.Filter ? "Explore" : searchKeyword
          }
          onGoBack={
            filterState === FilterStates.Filter
              ? onClose
              : () => {
                  setFilterState(FilterStates.Filter);
                  setSearchKeyword("");
                }
          }
        />
        {filterState === FilterStates.Filter && (
          <div className={styles.container}>
            <SearchTextField
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.currentTarget.value)}
              className={styles.searchField}
              placeholder="What do you want to learn?"
              iconPlacement="start"
            />
            {/* <Typography
            color="text.secondary"
            variant="h6"
            className={styles.title}
          >
            Trending
          </Typography>
          <ZigzagArrowIcon /> */}
            <Typography
              variant="h6"
              color="text.secondary"
              className={styles.title}
            >
              Browse categories
            </Typography>
            <div className={styles.categoriesContainer}>
              {isLoadingCategories
                ? new Array(4)
                    .fill(1)
                    .map((each, idx) => (
                      <Skeleton
                        key={idx}
                        variant="rectangular"
                        className={styles.categoryLoader}
                      />
                    ))
                : categories.map((eachCategory) => (
                    <Tag
                      key={eachCategory.id}
                      label={eachCategory.name}
                      className={cx(styles.tag, styles.tagMotion)}
                      color={"info"}
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedCategory(eachCategory.id)}
                      classes={{ icon: styles.tagIcon }}
                    />
                  ))}
            </div>
            <Button
              disabled={!searchKeyword}
              onClick={() => setFilterState(FilterStates.Search)}
              className={styles.searchButton}
            >
              See result
            </Button>
          </div>
        )}
        {filterState === FilterStates.Search && (
          <div className={styles.searchContainer}>
            {isLoadingCourses ? (
              [1, 2, 3].map((each, idx) => (
                <Fragment key={each}>
                  <CourseLoader />
                  {idx !== courses.length - 1 && (
                    <Divider className={styles.divider} />
                  )}
                </Fragment>
              ))
            ) : (
              <>
                {courses.length === 0 ? (
                  <Typography>No result found</Typography>
                ) : (
                  courses.map((eachCourse, idx) => (
                    <Fragment key={eachCourse.id}>
                      <CourseDetails course={eachCourse} categoryName={""} />
                      {idx !== courses.length - 1 && (
                        <Divider className={styles.divider} />
                      )}
                    </Fragment>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </MobileModal>
      {selectedCategory && (
        <CategoryCoursesModal
          categoryId={selectedCategory}
          onClose={() => setSelectedCategory(undefined)}
        />
      )}
    </>
  );
};

export default MobileFilterSection;
